import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { ConsoleApiError, createConsoleService, type Account, type ClientCreationInput, type ConsoleService, type UsageFilters } from '../services/console';

type Notice = { id: number; message: string; tone: 'success' | 'error' };
type ConsoleContext = {
  account: Account | null;
  initializing: boolean;
  sessionError: string | null;
  api: ConsoleService;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  retrySession: () => Promise<void>;
  notify: (message: string, tone?: 'success' | 'error') => void;
};
const Context = createContext<ConsoleContext | null>(null);
const expired = (reason: unknown) => reason instanceof ConsoleApiError && (reason.status === 401 || reason.code === 'customer_access_denied');

export function ConsoleProvider({ children, service }: { children: ReactNode; service?: ConsoleService }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const noticeTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const generation = useRef(0);
  const accountRef = useRef(account);
  accountRef.current = account;

  const notify = useCallback((message: string, tone: 'success' | 'error' = 'success') => {
    const id = Date.now() + Math.random();
    setNotices(previous => [...previous.slice(-2), { id, message, tone }]);
    const timer = setTimeout(() => { setNotices(previous => previous.filter(notice => notice.id !== id)); noticeTimers.current.delete(timer); }, 4500);
    noticeTimers.current.add(timer);
  }, []);
  useEffect(() => () => { noticeTimers.current.forEach(clearTimeout); noticeTimers.current.clear(); }, []);

  const api = useMemo<ConsoleService>(() => {
    const adapter = service ?? createConsoleService();
    // Catch authentication failures from every page call, including reads and mutations.
    // The cookie remains exclusively managed by the backend.
    function guard<Args extends unknown[], Result>(operation: (...args: Args) => Promise<Result>, ownsSessionCheck = false) {
      return async (...args: Args): Promise<Result> => {
        const version = generation.current;
        try { return await operation(...args); }
        catch (reason) {
          if (!ownsSessionCheck && expired(reason) && version === generation.current) {
            generation.current++;
            const hadAccount = accountRef.current !== null;
            accountRef.current = null;
            setAccount(null);
            setSessionError(null);
            if (hadAccount) notify('Your session has ended. Please sign in again.', 'error');
          }
          throw reason;
        }
      };
    }
    return {
      readSession: guard(() => adapter.readSession(), true),
      login: guard((username: string, password: string) => adapter.login(username, password), true),
      logout: guard(() => adapter.logout()),
      getAccount: guard(() => adapter.getAccount()),
      getWallet: guard(() => adapter.getWallet()),
      listKeys: guard((page?: number, pageSize?: number) => adapter.listKeys(page, pageSize)),
      createKey: guard((name: string) => adapter.createKey(name)),
      revokeKey: guard((id: number) => adapter.revokeKey(id)),
      usageSummary: guard((filters?: UsageFilters) => adapter.usageSummary(filters)),
      usageHistory: guard((filters?: UsageFilters, page?: number, pageSize?: number) => adapter.usageHistory(filters, page, pageSize)),
      listClients: guard((page?: number, pageSize?: number) => adapter.listClients(page, pageSize)),
      createClient: guard((input: ClientCreationInput) => adapter.createClient(input)),
      listClientOperations: guard((page?: number, pageSize?: number) => adapter.listClientOperations(page, pageSize)),
      getClientOperation: guard((id: string) => adapter.getClientOperation(id)),
    };
  }, [service, notify]);

  const retrySession = useCallback(async () => {
    const version = ++generation.current;
    setInitializing(true);
    setSessionError(null);
    try {
      const result = await api.readSession();
      if (version === generation.current) { accountRef.current = result.account; setAccount(result.account); }
    } catch (reason) {
      if (version === generation.current) {
        accountRef.current = null;
        setAccount(null);
        if (!expired(reason)) setSessionError(reason instanceof Error ? reason.message : 'Unable to check your session.');
      }
    } finally {
      if (version === generation.current) setInitializing(false);
    }
  }, [api]);

  useEffect(() => {
    void retrySession();
    return () => { generation.current++; };
  }, [retrySession]);

  const signIn = useCallback(async (username: string, password: string) => {
    const version = ++generation.current;
    const result = await api.login(username, password);
    if (version === generation.current) {
      accountRef.current = result.account;
      setAccount(result.account);
      setSessionError(null);
    }
  }, [api]);
  const signOut = useCallback(async () => {
    const version = generation.current;
    await api.logout();
    if (version !== generation.current) return;
    generation.current++;
    accountRef.current = null;
    setAccount(null);
    setSessionError(null);
    setNotices([]);
  }, [api]);
  const refreshAccount = useCallback(async () => {
    const version = generation.current;
    const result = await api.getAccount();
    if (version === generation.current) { accountRef.current = result; setAccount(result); }
  }, [api]);

  return <Context.Provider value={{ account, initializing, sessionError, api, signIn, signOut, refreshAccount, retrySession, notify }}>
    {children}
    <div className="toast-region" aria-live="polite" aria-atomic="false">{notices.map(notice => <div className={`toast toast-${notice.tone}`} key={notice.id}>{notice.tone === 'success' ? <CheckCircle2 size={17}/> : <AlertCircle size={17}/>}<span>{notice.message}</span><button className="icon-button" onClick={() => setNotices(previous => previous.filter(item => item.id !== notice.id))} aria-label="Dismiss notification"><X size={15}/></button></div>)}</div>
  </Context.Provider>;
}

export function useConsole() {
  const value = useContext(Context);
  if (!value) throw new Error('useConsole must be used inside ConsoleProvider.');
  return value;
}
